import { convertedQtyFormatterConfig } from '@/config/config';
import { mergeRefs } from '@/utils/common';
import useLanguage from '@/utils/hooks/useLanguage';
import cn from 'classnames';
import { ComponentProps, forwardRef, useEffect, useRef } from 'react';
import { useLocale, useNumberField } from 'react-aria';
import { useNumberFieldState } from 'react-stately';
import PortalCurrencyIcon from '../common/PortalCurrencyIcon';

interface Props extends ComponentProps<'input'> {
    min?: number;
    step?: number;
    label?: string;
    className?: string;
    defaultValue?: string | number;
    currency?: string;
}

const NumberInputDisplay = forwardRef<HTMLInputElement, Props>((props, ref: any) => {
    const { name = '', label, value, disabled, className, currency } = props;

    const inputRef = useRef(null);
    const { locale } = useLocale();
    const { dir } = useLanguage();

    const state = useNumberFieldState({ locale, formatOptions: convertedQtyFormatterConfig });
    const { groupProps, inputProps } = useNumberField({ label: label || name }, state, inputRef);

    useEffect(() => {
        if (value) state.setNumberValue(+value);
    }, [value]);

    return (
        <div {...groupProps} className="relative flex">
            {currency && (
                <div
                    className={cn(
                        'flex justify-center items-center flex-1 basis-0 text-sm text-brand-text bg-brand-background-dark border-brand-neutral-700',
                        {
                            'text-brand-neutral-400 cursor-not-allowed': disabled,
                            'pr-3 border border-l-0 rounded-r-md': dir === 'rtl',
                            'pl-3 border border-r-0 rounded-l-md': dir === 'ltr'
                        }
                    )}>
                    <PortalCurrencyIcon show={currency} disabled={disabled} />
                </div>
            )}
            <input
                {...inputProps}
                ref={mergeRefs(inputRef, ref)}
                value={state.inputValue}
                placeholder={props.placeholder}
                autoComplete="off"
                className={cn(
                    'appearance-none block w-full p-2 bg-brand-background-dark border border-brand-neutral-700 text-sm placeholder-brand-neutral-500 focus:outline-none',
                    // 'appearance-none block w-full px-3 py-2 bg-brand-background-dark border border-brand-neutral-700 rounded-md text-sm placeholder-neutral-500 focus:outline-none focus:ring-brand-green focus:border-brand-green',
                    {
                        'text-brand-text': !className,
                        'text-brand-neutral-400 cursor-not-allowed': disabled,
                        'pr-1.5 pl-3 border-r-0 rounded-l-md': dir === 'rtl',
                        'pl-1.5 pr-3 border-l-0 rounded-r-md': dir === 'ltr'
                    },
                    className
                )}
                disabled={disabled}
            />
            <div className="absolute right-2 flex flex-col h-full justify-center text-xs text-brand-text w-5">
                <button
                    type="button"
                    className={cn('px-0', {
                        'text-brand-neutral-400 cursor-not-allowed': disabled,
                        'hover:text-brand-green-light': !disabled
                    })}>
                    &#x25B2;
                </button>
                <button
                    type="button"
                    className={cn('px-0', {
                        'text-brand-neutral-400 cursor-not-allowed': disabled,
                        'hover:text-brand-green-light': !disabled
                    })}>
                    &#x25BC;
                </button>
            </div>
        </div>
    );
});

NumberInputDisplay.displayName = 'NumberInputDisplay';

export default NumberInputDisplay;
